// ============================================================
// ENGINE V2 — DECISION ENGINE
// ============================================================
// Validates career path node selection against all guards
// before the student is allowed to start or traverse to a node.
//
// Guards enforced:
//   1. Max active nodes ceiling
//   2. No duplicate active nodes at the same alias
//   3. Broad-level categories (STEM/Humanities/Management) can
//      only have one active broad node per category at a time
//   4. An active node cannot be re-selected
//
// Traversal (advancing from a parent to a child node):
//   When a student advances, the parent node is marked 'completed'
//   so its broad-category slot opens for re-use at that level.
// ============================================================

import type {
  StudentProfile,
  CareerPathNode,
  ActiveNode,
  DecisionResult,
} from './types';
import { MAX_ACTIVE_NODES } from './constants';

// ─── GUARD VALIDATION ────────────────────────────────────────

function validateGuards(
  student: StudentProfile,
  targetNode: CareerPathNode
): { passed: boolean; reason?: string } {
  const currentlyActive = student.activeNodes.filter(n => n.status === 'active');

  // Guard 1: Max active nodes
  if (currentlyActive.length >= MAX_ACTIVE_NODES) {
    return {
      passed: false,
      reason: `Maximum of ${MAX_ACTIVE_NODES} active career path nodes reached. Complete or abandon a path before starting a new one.`,
    };
  }

  // Guard 2: No duplicate node — same nodeId cannot be active twice
  const isDuplicate = currentlyActive.some(n => n.nodeId === targetNode.nodeId);
  if (isDuplicate) {
    return {
      passed: false,
      reason: `"${targetNode.name}" is already an active career path node.`,
    };
  }

  // Guard 3: No duplicate alias — same level path cannot be active twice
  // e.g. can't have two STEM.Engineering nodes active simultaneously
  const aliasConflict = currentlyActive.some(n => n.alias === targetNode.alias);
  if (aliasConflict) {
    return {
      passed: false,
      reason: `A "${targetNode.alias}" node is already active. You cannot start the same path level twice.`,
    };
  }

  // Guard 4: Broad-level nodes are one-per-category
  // A student cannot have two active broad nodes in the same category.
  // (They can have STEM broad + Humanities broad, but not two STEM broads.)
  if (targetNode.level === 'broad') {
    const broadConflict = currentlyActive.some(
      n => n.level === 'broad' && n.broadCategory === targetNode.broadCategory
    );
    if (broadConflict) {
      return {
        passed: false,
        reason: `A ${targetNode.broadCategory} broad-level path is already active. Advance to a field-level node first to free this slot.`,
      };
    }
  }

  return { passed: true };
}

// ─── SEED TRAITS FROM GENERAL PROFILE ────────────────────────
// When a student starts a new node, seed their initial trait scores
// from their general trait profile. This means existing knowledge
// carries over instead of starting every new path from zero.

function seedTraitsFromGeneralProfile(
  node: CareerPathNode,
  student: StudentProfile
): CareerPathNode['traits'] {
  return node.traits.map(trait => {
    const generalTrait = student.generalTraits.find(
      g => g.traitId === trait.traitId
    );
    return {
      ...trait,
      // Use general trait score as the starting point.
      // If no general trait exists, student starts at 0 for this trait.
      score: generalTrait ? generalTrait.score : 0,
    };
  });
}

// ─── DECISION ENGINE — PATH SELECTION ────────────────────────
// Called when a student manually selects a new career path node.

export function runDecisionEngine(
  student: StudentProfile,
  targetNode: CareerPathNode
): DecisionResult {
  const validation = validateGuards(student, targetNode);

  if (!validation.passed) {
    return { allowed: false, reason: validation.reason };
  }

  const newActiveNode: ActiveNode = {
    nodeId:        targetNode.nodeId,
    alias:         targetNode.alias,
    level:         targetNode.level,
    broadCategory: targetNode.broadCategory,
    status:        'active',
    traits:        seedTraitsFromGeneralProfile(targetNode, student),
    startedAt:     new Date(),
    lastUpdatedAt: new Date(),
  };

  return {
    allowed: true,
    updatedActiveNodes: [...student.activeNodes, newActiveNode],
  };
}

// ─── DECISION ENGINE — TRAVERSAL ─────────────────────────────
// Called when a student advances from a parent node to a child node.
// e.g. STEM → STEM.Engineering → STEM.Engineering.Mechanical
//
// On traversal:
//   - The parent node is marked 'completed' (freeing its guard slot)
//   - The child node is added as a new 'active' node
//   - Normal guards still apply to the child

export function traverseCareerPath(
  student: StudentProfile,
  targetNode: CareerPathNode
): DecisionResult {
  // Confirm the student has the parent node active
  const parentIsActive =
    targetNode.parentId !== null &&
    student.activeNodes.some(
      n => n.nodeId === targetNode.parentId && n.status === 'active'
    );

  if (targetNode.parentId !== null && !parentIsActive) {
    return {
      allowed: false,
      reason: `You must have the parent career path node active before advancing to "${targetNode.name}".`,
    };
  }

  // Run standard guards against the child node
  const validation = validateGuards(student, targetNode);
  if (!validation.passed) {
    return { allowed: false, reason: validation.reason };
  }

  // Mark parent as completed, add child as active
  const nodesWithCompletedParent = student.activeNodes.map(n =>
    n.nodeId === targetNode.parentId
      ? { ...n, status: 'completed' as const }
      : n
  );

  const newActiveNode: ActiveNode = {
    nodeId:        targetNode.nodeId,
    alias:         targetNode.alias,
    level:         targetNode.level,
    broadCategory: targetNode.broadCategory,
    status:        'active',
    traits:        seedTraitsFromGeneralProfile(targetNode, student),
    startedAt:     new Date(),
    lastUpdatedAt: new Date(),
  };

  return {
    allowed: true,
    updatedActiveNodes: [...nodesWithCompletedParent, newActiveNode],
  };
}
